import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _noop from "lodash/noop";
import _get from "lodash/get";
var _excluded = ["analytics", "children", "enableAnalytics", "enableTracking", "expanded", "id", "index", "sectionCategory", "title", "onSelect", "headingColor"],
    _excluded2 = ["initiallyExpanded", "onSelect"];
import "core-js/modules/es6.regexp.replace.js";
import "core-js/modules/es6.object.keys.js";
import BSPanel from 'react-bootstrap/lib/Panel';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import classNames from 'classnames'; // eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration

import Navigateup from '@nerdwallet/react-icon/gizmo/Navigateup';
import Navigatedown from '@nerdwallet/react-icon/gizmo/Navigatedown';
import classes from '@nerdwallet/base-styles/classes';
import analyticsModule from '@nerdwallet/analytics';
import { Impression, ImpressionContext } from '@nerdwallet/analytics/react/impression';
import { elementInteraction } from '@nerdwallet/nw-client-lib/tracking';
import style from "./accordion.module.css";

var getElementName = function getElementName(name) {
  return name === null || name === void 0 ? void 0 : name.toLowerCase().replace(/ |\W/g, '_').substring(0, 64);
}; // schema valdation limits the name to 64 characters


var getInteractionString = function getInteractionString(expanded) {
  return expanded ? 'close' : 'open';
};

var getElementInteractionString = function getElementInteractionString(expanded) {
  return expanded ? 'COLLAPSE' : 'EXPAND';
};

var colorsMap = {
  'neutral-darkest': 'color-neutral-darkest',
  'neutral-darker': 'color-neutral-darker',
  white: 'color-white',
  teal: 'color-teal',
  green: 'color-green',
  'green-dark': 'color-green-dark'
};

var ControlledAccordionItem = function ControlledAccordionItem(_ref) {
  var _analytics$payload2, _analytics$payload3, _analytics$payload4;

  var analytics = _ref.analytics,
      children = _ref.children,
      enableAnalytics = _ref.enableAnalytics,
      enableTracking = _ref.enableTracking,
      expanded = _ref.expanded,
      id = _ref.id,
      index = _ref.index,
      sectionCategory = _ref.sectionCategory,
      title = _ref.title,
      onSelect = _ref.onSelect,
      headingColor = _ref.headingColor,
      panelProps = _objectWithoutProperties(_ref, _excluded);

  var Icon = expanded ? Navigateup : Navigatedown;

  var handleToggle = function handleToggle(evt, impressionId) {
    var content = _get(evt, 'currentTarget.textContent');

    var elementInteractionString = getElementInteractionString(expanded);
    var interactionString = getInteractionString(expanded);

    if (enableTracking) {
      elementInteraction({
        elementCategory: 'button',
        elementContent: content,
        elementName: 'AccordionItem',
        interactionType: elementInteractionString,
        sectionCategory: sectionCategory
      });
    }

    if (analytics !== null && analytics !== void 0 && analytics.enabled || enableAnalytics) {
      var _analytics$payload;

      var elementName = getElementName("accordion_".concat(content));
      var elementNameFromProps = getElementName(analytics === null || analytics === void 0 ? void 0 : (_analytics$payload = analytics.payload) === null || _analytics$payload === void 0 ? void 0 : _analytics$payload.elementName);
      var payload = {
        action: interactionString,
        entity_name: elementNameFromProps || elementName,
        interaction_type: 'press'
      };

      if (impressionId) {
        payload.impression_id = impressionId;
      }

      analyticsModule.track('element_interacted', payload);
    }

    onSelect();
  };

  var impressionName = (analytics === null || analytics === void 0 ? void 0 : (_analytics$payload2 = analytics.payload) === null || _analytics$payload2 === void 0 ? void 0 : _analytics$payload2.elementName) || 'accordion_item';
  var headingColorClass = colorsMap[headingColor] ? classes(colorsMap[headingColor]) : '';
  return /*#__PURE__*/React.createElement(Impression, {
    entityName: getElementName(impressionName),
    payload: {
      element_position: index,
      section_name: (analytics === null || analytics === void 0 ? void 0 : (_analytics$payload3 = analytics.payload) === null || _analytics$payload3 === void 0 ? void 0 : _analytics$payload3.sectionName) || getElementName(id),
      section_position: (analytics === null || analytics === void 0 ? void 0 : (_analytics$payload4 = analytics.payload) === null || _analytics$payload4 === void 0 ? void 0 : _analytics$payload4.sectionPosition) || index
    },
    enabled: (analytics === null || analytics === void 0 ? void 0 : analytics.enabled) || enableAnalytics
  }, /*#__PURE__*/React.createElement(ImpressionContext.Consumer, null, function (impressionContext) {
    return /*#__PURE__*/React.createElement(BSPanel, _extends({
      id: id || analyticsModule.uuid(),
      expanded: expanded,
      className: style.panelContainer,
      onToggle: function onToggle() {} // BSPanel throws a warning if onToggle isn't supplied; it's a no-op as we invoke from the BSPanel.title

    }, panelProps), title && /*#__PURE__*/React.createElement(BSPanel.Heading, {
      className: classNames([style.title, headingColorClass]) // onTouchStart listener is required for mobile iOS to recognize touch :active states
      ,
      onTouchStart: _noop,
      onClick: function onClick(evt) {
        return handleToggle(evt, impressionContext === null || impressionContext === void 0 ? void 0 : impressionContext.impressionId);
      },
      componentClass: "button",
      role: "button",
      toggle: "true"
    }, title, /*#__PURE__*/React.createElement(Icon, {
      className: style.icon
    })), /*#__PURE__*/React.createElement(BSPanel.Body, {
      className: style.body,
      collapsible: true
    }, children));
  }));
};

ControlledAccordionItem.propTypes = {
  /**
   * The content string within the AccordionItem.
   */
  children: PropTypes.node.isRequired,

  /**
   * Unique identifier supplied to Bootstrap PanelGroup. In order to properly initialize
   * the PanelGroup in a way that is accessible to assistive technologies (such as screen readers) an `id`
   * is required.
   */
  id: PropTypes.string,

  /**
   * The position of the accordion item in the accordion list.
   */
  index: PropTypes.number.isRequired,

  /**
   * Whether we would like dispatch when bedrock analytics when the panel is expanded or collapsed.
   * @deprecated  Use the enabled flag the analytics prop.
   */
  enableAnalytics: PropTypes.bool,

  /**
   * The content used by that analytics module and the flag to determine if analytics is sent
   */
  analytics: PropTypes.shape({
    enabled: PropTypes.bool,
    payload: PropTypes.shape({
      elementName: PropTypes.string,
      sectionName: PropTypes.string,
      sectionPosition: PropTypes.number
    })
  }),

  /**
   * Whether we would like to dispatch `formOpen` and `formCancel` events when the panel is expanded or collapsed.
   */
  enableTracking: PropTypes.bool,

  /**
   * Should the initial state of this accordion be expanded or not
   */
  expanded: PropTypes.bool,

  /**
   * Callback to be invoked when an AccordionItem is selected.
   */
  onSelect: PropTypes.func,

  /**
   * Used for element interaction tracking to indicate where on page the Accordion is.
   */
  sectionCategory: PropTypes.string,

  /**
   * The header string of the AccordionItem.
   */
  title: PropTypes.node.isRequired,

  /**
   * Text color string for the AccordionItem heading
   */
  headingColor: PropTypes.oneOf(Object.keys(colorsMap))
};
ControlledAccordionItem.defaultProps = {
  analytics: {
    enabled: false
  },
  enableAnalytics: false,
  enableTracking: false,
  expanded: false,
  id: null,
  onSelect: function onSelect() {},
  sectionCategory: null
};

var AccordionItem = function AccordionItem(_ref2) {
  var initiallyExpanded = _ref2.initiallyExpanded,
      onSelect = _ref2.onSelect,
      props = _objectWithoutProperties(_ref2, _excluded2);

  var _useState = useState(initiallyExpanded),
      _useState2 = _slicedToArray(_useState, 2),
      isExpanded = _useState2[0],
      setIsExpanded = _useState2[1];

  var handleToggle = function handleToggle() {
    onSelect();
    setIsExpanded(function (wasExpanded) {
      return !wasExpanded;
    });
  };

  return /*#__PURE__*/React.createElement(ControlledAccordionItem, _extends({
    expanded: isExpanded,
    onSelect: handleToggle
  }, props));
};

AccordionItem.propTypes = {
  /**
   * The content used by that analytics module and the flag to determine if analytics is sent
   */
  analytics: PropTypes.shape({
    enabled: PropTypes.bool,
    payload: PropTypes.shape({
      elementName: PropTypes.string
    })
  }),

  /**
   * The content string within the AccordionItem.
   */
  children: PropTypes.node.isRequired,

  /**
   * Whether we would like dispatch when bedrock analytics when the panel is expanded or collapsed.
   * @deprecated  Use the enabled flag the analytics prop.
   */
  enableAnalytics: PropTypes.bool,

  /**
   * Whether we would like to dispatch `formOpen` and `formCancel` events when the panel is expanded or collapsed.
   */
  enableTracking: PropTypes.bool,

  /**
   * Unique identifier supplied to Bootstrap PanelGroup. In order to properly initialize
   * the PanelGroup in a way that is accessible to assistive technologies (such as screen readers) an `id`
   * is required.
   */
  id: PropTypes.string,

  /**
   * The position of the accordion item in the accordion list.
   */
  index: PropTypes.number.isRequired,

  /**
   * Should the initial state of this accordion be expanded or not
   */
  initiallyExpanded: PropTypes.bool,

  /**
   * Callback to be invoked when an AccordionItem is selected.
   */
  onSelect: PropTypes.func,

  /**
   * Used for element interaction tracking to indicate where on page the Accordion is.
   */
  sectionCategory: PropTypes.string,

  /**
   * The header string of the AccordionItem.
   */
  title: PropTypes.node.isRequired
};
AccordionItem.defaultProps = {
  analytics: {
    enabled: false
  },
  enableAnalytics: false,
  enableTracking: false,
  id: null,
  initiallyExpanded: false,
  onSelect: function onSelect() {},
  sectionCategory: null
};
export { ControlledAccordionItem };
export default AccordionItem;